/**
 * Edit Tool Combo Template Drawer
 *
 * Right-side drawer that lets the admin edit a global tool combo template
 * directly from the agent config page, without navigating to System Settings.
 *
 * Important: templates are GLOBAL — saving here updates the shared template
 * for every agent referencing it. The drawer surfaces a usage-count banner
 * (agents currently bound to this template) plus a confirm-before-save guard.
 */

import React, { useEffect, useMemo, useState } from "react";
import { X, AlertTriangle, Save, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/Button";
import {
  useToolComboConfig,
  useUpdateToolComboConfig,
  useToolComboTemplateUsage,
  useToolEnhancementConfig,
} from "@/features/system-settings/hooks/useSystemConfig";
import { useToolsQuery } from "@/lib/queries";
import { useI18n } from "@/i18n";
import {
  ToolComboTemplateEditor,
  type ToolComboAvailableTool,
} from "@/features/system-settings/components/ToolComboTemplateEditor";
import type { ToolComboTemplate } from "@/types/system";

interface Props {
  templateId: string | null;
  open: boolean;
  onClose: () => void;
}

export const EditToolComboTemplateDrawer: React.FC<Props> = ({
  templateId,
  open,
  onClose,
}) => {
  const { messages, language } = useI18n();
  const isChinese = language === "zh";
  const tSystem = messages.systemSettings?.toolCombo;

  const localizedStrings = {
    title: isChinese ? "编辑工具组合模板" : "Edit Tool Combo Template",
    close: isChinese ? "关闭" : "Close",
    cancel: isChinese ? "取消" : "Cancel",
    save: isChinese ? "保存模板" : "Save Template",
    saving: isChinese ? "保存中..." : "Saving...",
    loading: isChinese ? "加载模板中..." : "Loading template...",
    notFound: isChinese ? "未找到模板" : "Template not found",
    globalWarning: isChinese
      ? "此模板是全局共享配置"
      : "This template is shared globally",
    usedByCount: (n: number) =>
      isChinese
        ? `当前被 ${n} 个 Agent 引用`
        : `Currently used by ${n} agent${n === 1 ? "" : "s"}`,
    usedByLabel: isChinese
      ? "引用此模板的 Agent："
      : "Agents using this template:",
    saveImpactNotice: (n: number) =>
      isChinese
        ? `保存后将立即影响 ${n} 个 Agent 的工具调用行为`
        : `Saving will immediately affect tool behavior in ${n} agent${n === 1 ? "" : "s"}`,
    confirmSavePrefix: isChinese
      ? "此模板被以下 Agent 使用："
      : "This template is used by the following agents:",
    confirmSaveSuffix: isChinese
      ? "\n\n确认保存？修改会立即生效。"
      : "\n\nConfirm save? Changes apply immediately.",
    saveFailed: isChinese ? "保存失败" : "Save failed",
  };

  const { data: toolsData } = useToolsQuery();
  const { data: enhancementConfig } = useToolEnhancementConfig();
  const { data: fullConfig, isLoading: configLoading } = useToolComboConfig();
  const {
    data: usage,
    isLoading: usageLoading,
    isError: usageError,
  } = useToolComboTemplateUsage(open ? templateId : null);
  const updateConfig = useUpdateToolComboConfig();

  const [editingTemplate, setEditingTemplate] =
    useState<ToolComboTemplate | null>(null);

  // Snapshot template into local state when drawer opens or templateId changes
  useEffect(() => {
    if (!open || !templateId || !fullConfig?.templates) {
      setEditingTemplate(null);
      return;
    }
    const source = fullConfig.templates[templateId];
    if (source) {
      setEditingTemplate(JSON.parse(JSON.stringify(source)));
    }
  }, [open, templateId, fullConfig]);

  const availableTools = useMemo<ToolComboAvailableTool[]>(() => {
    if (!toolsData) return [];
    const enhancementTools = enhancementConfig?.tools || {};
    const tools: ToolComboAvailableTool[] = [];

    toolsData.builtin_toolkits?.forEach((toolkit) => {
      const toolName = toolkit.name.replace("builtin:", "");
      const enhancement = enhancementTools[toolName];
      tools.push({
        name: toolName,
        description: toolkit.description,
        category: toolkit.category || "other",
        type: "builtin",
        displayNameCn: enhancement?.display_name_cn,
        displayNameEn: enhancement?.display_name_en,
        descriptionCn: enhancement?.description_cn,
        descriptionEn: enhancement?.description_en,
      });
    });

    toolsData.custom_tools?.forEach((tool) => {
      const enhancement = enhancementTools[tool.name];
      tools.push({
        name: tool.name,
        description: tool.description,
        category: tool.category || "custom",
        type: "custom",
        displayNameCn: enhancement?.display_name_cn,
        displayNameEn: enhancement?.display_name_en,
        descriptionCn: enhancement?.description_cn,
        descriptionEn: enhancement?.description_en,
      });
    });

    return tools.sort((a, b) => a.name.localeCompare(b.name));
  }, [toolsData, enhancementConfig]);

  const handleSave = async () => {
    if (!editingTemplate || !fullConfig?.templates) return;

    const usageCount = usage?.usage_count ?? 0;
    const agentList = (usage?.agent_types ?? []).join("\n  - ");
    const confirmMessage =
      usageCount > 0
        ? `${localizedStrings.confirmSavePrefix}\n  - ${agentList}${localizedStrings.confirmSaveSuffix}`
        : isChinese
          ? "确认保存？"
          : "Confirm save?";

    if (!confirm(confirmMessage)) return;

    try {
      const nextTemplates = {
        ...fullConfig.templates,
        [editingTemplate.id]: editingTemplate,
      };
      await updateConfig.mutateAsync({ templates: nextTemplates });
      onClose();
    } catch (error) {
      alert(`${localizedStrings.saveFailed}: ${error}`);
    }
  };

  if (!open) return null;

  const usageCount = usage?.usage_count ?? 0;
  const agentTypes = usage?.agent_types ?? [];

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div
        className="absolute inset-0 bg-black/40"
        onClick={updateConfig.isPending ? undefined : onClose}
      />
      <div className="relative w-full max-w-3xl bg-white shadow-2xl flex flex-col h-full">
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {localizedStrings.title}
            </h2>
            {templateId && (
              <p className="text-xs text-gray-500 font-mono mt-0.5">
                {templateId}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            disabled={updateConfig.isPending}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {!usageLoading &&
          (usageError ? (
            <div className="px-5 py-3 bg-red-50 border-b border-red-200">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-700">
                  {isChinese
                    ? "无法获取模板使用信息，请稍后重试"
                    : "Failed to load template usage info. Please retry."}
                </p>
              </div>
            </div>
          ) : (
            <div className="px-5 py-3 bg-amber-50 border-b border-amber-200">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-amber-900">
                    {localizedStrings.globalWarning} ·{" "}
                    {localizedStrings.usedByCount(usageCount)}
                  </p>
                  {agentTypes.length > 0 && (
                    <div className="mt-1.5">
                      <span className="text-xs text-amber-700">
                        {localizedStrings.usedByLabel}
                      </span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {agentTypes.map((agentType) => (
                          <span
                            key={agentType}
                            className="text-xs font-mono bg-amber-100 text-amber-800 px-2 py-0.5 rounded"
                          >
                            {agentType}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {usageCount > 0 && (
                    <p className="text-xs text-amber-700 mt-1.5">
                      {localizedStrings.saveImpactNotice(usageCount)}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}

        <div className="flex-1 overflow-y-auto">
          {configLoading || !editingTemplate ? (
            <div className="flex items-center justify-center h-full text-gray-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              {configLoading
                ? localizedStrings.loading
                : localizedStrings.notFound}
            </div>
          ) : (
            <ToolComboTemplateEditor
              template={editingTemplate}
              availableTools={availableTools}
              isChinese={isChinese}
              t={tSystem}
              onChange={setEditingTemplate}
            />
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t bg-gray-50">
          <Button
            variant="secondary"
            size="sm"
            onClick={onClose}
            disabled={updateConfig.isPending}
          >
            {localizedStrings.cancel}
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={!editingTemplate || updateConfig.isPending}
          >
            <Save className="w-4 h-4 mr-1" />
            {updateConfig.isPending
              ? localizedStrings.saving
              : localizedStrings.save}
          </Button>
        </div>
      </div>
    </div>
  );
};
